import { API_CONFIG } from '../configuration/api';

export interface Friend {
  userId: string;
  displayName: string;
  friendshipDate: number;
  currentStreak: number;
  totalGamesPlayed: number;
  totalWins: number;
  winRate: number;
}

export interface FriendInvitation {
  invitationId: string;
  fromUserId: string;
  fromUserName: string;
  toEmail?: string;
  toUserId?: string;
  status: 'pending' | 'accepted' | 'declined';
  createdAt: number;
  expiresAt: number;
  inviteType: 'email' | 'link';
}

export interface FriendProfile {
  userId: string;
  displayName: string;
  friendshipDate: number;
  statistics: {
    currentStreak: number;
    longestStreak: number;
    totalGamesPlayed: number;
    totalWins: number;
    totalLosses: number;
    averageScore: number;
    averageGuesses: number;
    winRate: number;
    perfectGames: number;
    totalQuestions: number;
    totalTrivia: number;
    lastPlayedDate?: number;
  };
  achievements: Array<{
    achievementId: string;
    unlockedAt: number;
    progress: number;
  }>;
  recentActivity: {
    lastPlayed?: number;
  };
}

class FriendsManager {
  /**
   * Get list of user's friends
   */
  async getFriends(token: string): Promise<Friend[]> {
    try {
      const response = await fetch(API_CONFIG.buildUrl('/friends', true), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to get friends: ${response.status}`, errorText);
        throw new Error(`Failed to get friends: ${response.statusText}`);
      }

      const data = await response.json();
      return data.friends || [];
    } catch (error) {
      console.error('Error getting friends:', error);
      throw error;
    }
  }

  /**
   * Get a friend's profile and stats
   */
  async getFriendProfile(friendId: string, token: string): Promise<FriendProfile> {
    try {
      const response = await fetch(API_CONFIG.buildUrl(`/friends/${friendId}`, true), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to get friend profile: ${response.status}`, errorText);
        throw new Error(`Failed to get friend profile: ${response.statusText}`);
      }

      const data = await response.json();
      return data.profile;
    } catch (error) {
      console.error('Error getting friend profile:', error);
      throw error;
    }
  }

  /**
   * Send a friend invitation
   */
  async sendFriendInvite(inviteData: { toEmail?: string; inviteType: 'email' | 'link' }, token: string): Promise<{ success: boolean; inviteLink?: string; message: string }> {
    try {
      const response = await fetch(API_CONFIG.buildUrl('/friends/invites', true), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(inviteData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to send friend invite: ${response.status}`, errorText);
        throw new Error(`Failed to send friend invite: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error sending friend invite:', error);
      throw error;
    }
  }

  /**
   * Get pending friend invitations
   */
  async getPendingInvitations(token: string): Promise<FriendInvitation[]> {
    try {
      const response = await fetch(API_CONFIG.buildUrl('/friends/invites', true), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to get pending invitations: ${response.status}`, errorText);
        throw new Error(`Failed to get pending invitations: ${response.statusText}`);
      }

      const data = await response.json();
      return data.invitations || [];
    } catch (error) {
      console.error('Error getting pending invitations:', error);
      throw error;
    }
  }

  /**
   * Accept a friend invitation
   */
  async acceptInvitation(invitationId: string, token: string): Promise<{ success: boolean; friendId: string }> {
    try {
      const response = await fetch(API_CONFIG.buildUrl('/friends/invites/accept', true), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ invitationId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to accept invitation: ${response.status}`, errorText);
        throw new Error(`Failed to accept invitation: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error accepting invitation:', error);
      throw error;
    }
  }

  /**
   * Decline a friend invitation
   */
  async declineInvitation(invitationId: string, token: string): Promise<{ success: boolean }> {
    try {
      const response = await fetch(API_CONFIG.buildUrl('/friends/invites/decline', true), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ invitationId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to decline invitation: ${response.status}`, errorText);
        throw new Error(`Failed to decline invitation: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error declining invitation:', error);
      throw error;
    }
  }

  /**
   * Remove a friend
   */
  async removeFriend(friendId: string, token: string): Promise<{ success: boolean }> {
    try {
      const response = await fetch(API_CONFIG.buildUrl(`/friends/${friendId}`, true), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to remove friend: ${response.status}`, errorText);
        throw new Error(`Failed to remove friend: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error removing friend:', error);
      throw error;
    }
  }

  /**
   * Send friend request by invite code
   */
  async sendFriendRequestByCode(inviteCode: string, token: string): Promise<{ success: boolean; friendId: string; friendName: string }> {
    try {
      const response = await fetch(API_CONFIG.buildUrl('/friends/add-by-code', true), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ inviteCode })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Failed to send friend request by code: ${response.status}`, errorText);

        // Parse error message if available
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || `Failed to send friend request: ${response.statusText}`);
        } catch (e) {
          throw new Error(`Failed to send friend request: ${response.statusText}`);
        }
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error sending friend request by code:', error);
      throw error;
    }
  }
}

export default new FriendsManager();
